/** Pontos de atenção: atrasados, cancelados no marketplace (embalados x não embalados), travados, sem prazo e
 * separação parada no Tiny ("Outros" esquecido há mais de 7 dias; fora do dia e fora de "Sem prazo"). */
import { Box, CardActionArea, Grid, Stack, Typography } from '@mui/material'
import type { AbrirLista, Alerta, Pedido } from './types'
import { atencaoDoAlerta, linhaParados, linhasCancelados } from './logic'

type Caixa = { alerta: Alerta; titulo: string; sub: string; fundo: string; borda: string; cor: string }
const CAIXAS: Caixa[] = [
  { alerta: 'atrasado', titulo: 'Atrasados', sub: 'prazo passou e não embalou', fundo: '#fef2f2', borda: '#fecaca', cor: '#b91c1c' },
  { alerta: 'aguardando_liberacao_hoje', titulo: 'Aguardando liberação com prazo hoje', sub: 'o ML ainda não liberou para o Tiny — acompanhar no ML', fundo: '#f5f3ff', borda: '#c4b5fd', cor: '#6d28d9' },
  { alerta: 'cancelado_mk', titulo: 'Cancelados no marketplace', sub: '', fundo: '#fff7ed', borda: '#fed7aa', cor: '#c2410c' },
  { alerta: 'travado', titulo: 'Travados em separação', sub: 'há mais de 2h sem andar', fundo: '#fffbeb', borda: '#fde68a', cor: '#b45309' },
  { alerta: 'sem_prazo', titulo: 'Sem prazo', sub: 'a API ainda não trouxe a data', fundo: '#f8fafc', borda: '#e2e8f0', cor: '#475569' },
  { alerta: 'parado_tiny', titulo: 'Separação parada no Tiny', sub: '"Outros" não embalado há mais de 7 dias — conferir no Tiny (equipe inteira)', fundo: '#f5f3ff', borda: '#ddd6fe', cor: '#6d28d9' },
]

/** Caixa dos cancelados: duas linhas e cada uma abre a lista (a gaveta mostra nº do pedido, nº do envio e canal).
 * "Embalados" vem em destaque quando tem pedido: o pacote está pronto e tem que sair da carga. Essa linha é da
 * equipe inteira (`canceladosEmbalados`, sem filtros); "Ainda não embalados" segue os filtros. */
function Cancelados({ c, pedidos, canceladosEmbalados, abrirLista }: {
  c: Caixa; pedidos: Pedido[]; canceladosEmbalados?: Pedido[]; abrirLista: AbrirLista
}) {
  const { embalados, decora, naoEmbalados } = linhasCancelados(pedidos, canceladosEmbalados)
  const linhas = [
    { titulo: 'Embalados — retirar da carga', ps: embalados, destaque: embalados.length > 0 },
    // Shopee Decora cancelada com etiqueta: o painel não vê se já foi embalada (só aparece quando tem)
    ...(decora.length ? [{ titulo: 'Shopee Decora com etiqueta — retirar/não separar (conferir no UpSeller)', ps: decora, destaque: true }] : []),
    { titulo: 'Ainda não embalados — não separar', ps: naoEmbalados, destaque: false },
  ]
  return (
    <Box sx={{ height: '100%', borderRadius: 3, p: 1, bgcolor: c.fundo, border: 1, borderColor: c.borda, color: c.cor }}>
      <Typography sx={{ fontWeight: 600, fontSize: 13, px: 0.5 }}>{c.titulo}</Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {linhas.map((l) => (
          <CardActionArea key={l.titulo} onClick={() => abrirLista(`Cancelados · ${l.titulo}`, l.ps)}
            sx={{ borderRadius: 2, px: 0.75, py: 0.5, ...(l.destaque ? { bgcolor: '#b91c1c', color: '#fff' } : {}) }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ font: '800 26px Outfit', lineHeight: 1.1, minWidth: 30, fontVariantNumeric: 'tabular-nums' }}>{l.ps.length}</Typography>
              <Typography sx={{ fontWeight: l.destaque ? 700 : 600, fontSize: 12.5, lineHeight: 1.25 }}>{l.titulo}</Typography>
            </Stack>
          </CardActionArea>
        ))}
      </Stack>
    </Box>
  )
}

/** `pedidos` = baseAtencao: os filtros da tela menos a data máxima e o filtro de alerta (senão as caixas zerariam).
 * `canceladosEmbalados` = dados.cancelados_embalados e `parados` = dados.parados_tiny (equipe inteira, não seguem filtro). */
export function Atencao({ pedidos, canceladosEmbalados, parados, abrirLista, comRetidos = false }: {
  pedidos: Pedido[]; canceladosEmbalados?: Pedido[]; parados?: Pedido[]; abrirLista: AbrirLista
  /** Há ML retido (situação 0) nos dados: mostra a caixa "Aguardando liberação" (flag desligada = caixas de antes). */
  comRetidos?: boolean
}) {
  const caixas = CAIXAS.filter((c) => comRetidos || c.alerta !== 'aguardando_liberacao_hoje')
  return (
    <Grid container spacing={1.25}>
      {caixas.map((c) => {
        if (c.alerta === 'cancelado_mk') {
          return <Grid key={c.alerta} size={6}><Cancelados c={c} pedidos={pedidos} canceladosEmbalados={canceladosEmbalados} abrirLista={abrirLista} /></Grid>
        }
        // parada no Tiny: equipe inteira (dados.parados_tiny), sem filtros — lista de limpeza, como o retirar da carga
        const ps = c.alerta === 'parado_tiny' ? linhaParados(pedidos, parados) : atencaoDoAlerta(pedidos, c.alerta)
        return (
          <Grid key={c.alerta} size={6}>
            <CardActionArea onClick={() => abrirLista(c.titulo, ps)}
              sx={{ height: '100%', borderRadius: 3, p: 1.5, bgcolor: c.fundo, border: 1, borderColor: c.borda, color: c.cor }}>
              <Typography sx={{ font: '800 34px Outfit', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{ps.length}</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{c.titulo}</Typography>
              <Typography sx={{ fontSize: 11.5, opacity: 0.8 }}>{c.sub}</Typography>
            </CardActionArea>
          </Grid>
        )
      })}
    </Grid>
  )
}
